import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Props for the ConfirmDeleteDialog component
 */
interface ConfirmDeleteDialogProps {
  /** The trigger element (usually a button) */
  trigger: React.ReactNode;
  /** The title of the dialog */
  title: string;
  /** The description/warning message to display - can be a string or React component */
  description: string | React.ReactNode;
  /** The name/identifier that must be typed to confirm deletion */
  confirmationKey: string;
  /** Label for what is being deleted (e.g., "table name", "source name") */
  confirmationLabel?: string;
  /** Custom warning message - can be a string or React component (defaults to "This action cannot be undone.") */
  warningMessage?: string | React.ReactNode;
  /** Callback function to execute when delete is confirmed */
  onDelete: () => void;
  /** Whether the delete operation is in progress */
  isDeleting?: boolean;
  /** Optional open state control */
  open?: boolean;
  /** Optional callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Generic confirmation dialog for delete operations that requires typing a confirmation key
 */
export function ConfirmDeleteDialog({
  trigger,
  title,
  description,
  confirmationKey,
  confirmationLabel = 'name',
  warningMessage,
  onDelete,
  isDeleting = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ConfirmDeleteDialogProps) {
  const { t } = useLanguage();
  const [internalOpen, setInternalOpen] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState('');

  // Use provided warningMessage or default from translations
  const displayWarningMessage = warningMessage || t.dialogs.confirmDelete.defaultWarning;

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;

  /**
   * Handles the delete action
   */
  const handleDelete = () => {
    if (confirmationInput === confirmationKey) {
      onDelete();
    }
  };

  /**
   * Handles dialog close and resets form
   */
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setConfirmationInput('');
    }
  };

  const isConfirmationValid = confirmationInput === confirmationKey;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 space-y-2">
            <p className="text-sm text-destructive font-medium">{t.dialogs.confirmDelete.warning}</p>
            {typeof displayWarningMessage === 'string' ? (
              <p className="text-sm text-destructive font-medium">{displayWarningMessage}</p>
            ) : (
              <div className="text-sm text-destructive font-medium">{displayWarningMessage}</div>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-sm text-foreground">{t.dialogs.confirmDelete.typeToConfirm}</p>
            <div className="bg-muted px-2 py-1 rounded font-mono font-bold text-sm inline-block tracking-wide">
              {confirmationKey}
            </div>
            <Input
              id="confirmation"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder={`${t.dialogs.confirmDelete.enterPrefix} ${confirmationLabel}`}
              autoComplete="off"
              disabled={isDeleting}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isDeleting}>
            {t.dialogs.confirmDelete.cancel}
          </Button>
          <LoadingButton
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmationValid}
            isLoading={isDeleting}
          >
            {t.dialogs.confirmDelete.delete}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
